'use client';

import { cn } from '@fshr/shared-react';
import Button from '../button';
import { CustomPaginationProps } from '../carousel/overlay-carousel/overlay-carousel-component';

// Dot size configuration for different total dot counts
const DOT_SIZE_CONFIG = {
	1: [7], // 1 dot: 7x7
	2: [7, 6], // 2 dots: 7x7, 6x6
	3: [7, 6, 5], // 3 dots: 7x7, 6x6, 5x5
	4: [7, 7, 6, 5], // 4 dots: 7x7, 7x7, 6x6, 5x5
	5: [7, 7, 7, 6, 5], // 5 dots: 7x7, 7x7, 7x7, 6x6, 5x5
} as const;

// Helper function to get dot size based on position, total dots, and current page
const getDotSize = (index: number, totalDots: number, currentPage: number): string => {
	const sizes = DOT_SIZE_CONFIG[totalDots as keyof typeof DOT_SIZE_CONFIG];

	// Calculate the relative distance from the current page
	const distanceFromCurrent = Math.abs(index - currentPage);

	// Map distance to size index (0 = largest, 1 = second largest, etc.)
	const sizeIndex = Math.min(distanceFromCurrent, sizes.length - 1);
	const size = sizes[sizeIndex];

	return cn({
		'w-[7px] h-[7px]': size === 7,
		'w-[6px] h-[6px]': size === 6,
		'w-[5px] h-[5px]': size === 5,
	});
};

// Custom pagination component for image gallery component
// This component is used to display the pagination dots for the image gallery component, but the navigation is handled by the OverlayCarousel component
export const ImageGalleryPagination = ({
	currentPage,
	goToPage,
	totalItems,
}: CustomPaginationProps) => {
	// Limit dots to max 5 and ensure current page is always visible
	const maxDots = 5;
	const visibleDots = (() => {
		if (totalItems <= maxDots) {
			return Array.from({ length: totalItems }, (_, i) => i);
		}

		// Always ensure currentPage is in the middle when possible
		let start = currentPage - Math.floor(maxDots / 2);

		// Adjust start to keep dots within bounds
		if (start < 0) {
			start = 0;
		} else if (start + maxDots > totalItems) {
			start = totalItems - maxDots;
		}

		return Array.from({ length: maxDots }, (_, i) => start + i);
	})();

	if (totalItems <= 1) return null;

	return (
		<div className="absolute bottom-0 left-0 right-0 flex items-center justify-center h-[15%] padding-stack-s pointer-events-none">
			{/* Gradient layer for accessibility */}
			<div className="absolute w-full h-full inset-0 bg-linear-180 from-neutral-1700/0 to-img-overlay-80" />

			{/* Pagination dots */}
			<div className="relative z-10 flex items-center justify-center spacing-inline-2xs pointer-events-auto">
				{visibleDots.map((pageIndex, dotIndex) => (
					<Button
						intent="unstyled"
						onClick={() => {
							goToPage(pageIndex);
						}}
						key={pageIndex}
						className={cn(
							'rounded-full transition-all duration-300 cursor-pointer',
							getDotSize(dotIndex, visibleDots.length, currentPage),
							pageIndex === currentPage
								? 'bg-white shadow-sm'
								: 'bg-text-on-image-disabled hover:bg-text-on-image-disabled/80'
						)}
						aria-label={`Go to image ${pageIndex + 1}`}
					/>
				))}
			</div>
		</div>
	);
};
